import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'
import Shepherd from 'shepherd.js'

export default class OnboardingController extends Controller {
  static values = {
    autolaunchTourId: { type: String, default: '' },
    relaunchTourId: { type: String, default: '' },
    tours: { type: Object, default: {} }
  }

  connect () {
    this.tour = null
    this.activeTourId = null
    this.resumeSteps = {}
    this.navigatedResumeFor = {}
    this.completedThisSession = new Set()

    document.addEventListener('turbo:load', this.handleTurboLoad)
    document.addEventListener('turbo:morph', this.handleTurboMorph)

    this.maybeAutolaunch()
  }

  disconnect () {
    document.removeEventListener('turbo:load', this.handleTurboLoad)
    document.removeEventListener('turbo:morph', this.handleTurboMorph)
    this.cancelTour({ silent: true })
  }

  async start (event) {
    const id = event?.params?.id || this.relaunchTourIdValue
    if (!id) return
    await this.postProgress(`/onboarding/tours/${id}/relaunch`)
    this.launchTour(id)
  }

  // ---- private ----

  handleTurboLoad = () => {
    if (!this.tour) this.maybeAutolaunch()
  }

  handleTurboMorph = () => {
    if (!this.tour) return
    const currentStep = this.tour.getCurrentStep()
    if (!currentStep) return
    const selector = currentStep.options?.attachTo?.element
    if (selector && !document.querySelector(selector)) {
      this.cancelTour({ silent: true })
    }
  }

  maybeAutolaunch () {
    const id = this.autolaunchTourIdValue
    if (!id) return
    this.launchTour(id)
  }

  launchTour (id) {
    if (this.activeTourId === id) return // already running

    const definition = this.toursValue[id]
    if (!definition) return

    this.tour = this.buildTour(id, definition)
    this.activeTourId = id

    const navigationKey = `onboarding-navigate-${id}`
    const navigatedStep = sessionStorage.getItem(navigationKey)
    const candidateStep = this.resumeSteps[id] ?? navigatedStep ?? definition.resume_step
    const resumeStep = this.completedThisSession.has(id) ? null : candidateStep
    this.completedThisSession.delete(id)
    if (navigatedStep) sessionStorage.removeItem(navigationKey)

    if (resumeStep && resumeStep !== this.tour.steps[0]?.id) {
      this.tour.trigger('start')
      this.tour.focusedElBeforeOpen = document.activeElement
      this.tour.currentStep = null
      this.tour.setupModal()
      this.tour._setupActiveTour()
      this.tour.show(resumeStep)
    } else {
      this.tour.start()
    }
  }

  buildTour (id, definition) {
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        cancelIcon: { enabled: true },
        scrollTo: { behavior: 'smooth', block: 'center' }
      }
    })

    definition.steps.forEach((step, index) => {
      const stepConfig = {
        id: step.id,
        title: step.title,
        text: step.text,
        buttons: this.buttonsFor(index, definition.steps.length, tour)
      }

      if (step.attach_to?.element) {
        stepConfig.attachTo = {
          element: step.attach_to.element,
          on: step.attach_to.placement
        }
        stepConfig.beforeShowPromise = () =>
            this.maybeNavigate(step.navigate_to, id, step.id, tour)
                .then(() => this.maybeOpenSidebar(step.attach_to.element))
                .then(() => this.waitForElement(step.attach_to.element))
                .then(() => { if (this.tour !== tour) return new Promise(() => {}) })
      } else {
        stepConfig.beforeShowPromise = () =>
            this.maybeNavigate(step.navigate_to, id, step.id, tour)
                .then(() => { if (this.tour !== tour) return new Promise(() => {}) })
      }

      tour.addStep(stepConfig)
    })

    tour.on('show', ({ step }) => {
      if (this.navigatedResumeFor[id] === step.id) {
        delete this.navigatedResumeFor[id]
      } else {
        this.postProgress(`/onboarding/tours/${id}/advance`, { step_id: step.id })
      }
      this.resumeSteps[id] = step.id
    })
    tour.on('complete', () => {
      this.postProgress(`/onboarding/tours/${id}/complete`)
      delete this.resumeSteps[id]
      this.completedThisSession.add(id)
      this.activeTourId = null
      this.tour = null
    })
    tour.on('cancel', () => {
      const stepId = tour.getCurrentStep()?.id
      this.postProgress(`/onboarding/tours/${id}/dismiss`, stepId ? { step_id: stepId } : {})
      if (stepId) this.resumeSteps[id] = stepId
      this.activeTourId = null
      this.tour = null
    })

    return tour
  }

  buttonsFor (index, total, tour) {
    const buttons = []
    if (index > 0) {
      buttons.push({
        text: 'Back',
        secondary: true,
        action: () => tour.back(),
        classes: 'shepherd-button-secondary'
      })
    }
    if (index < total - 1) {
      buttons.push({
        text: 'Next',
        action: () => tour.next(),
        classes: 'shepherd-button-primary'
      })
    } else {
      buttons.push({
        text: 'Done',
        action: () => tour.complete(),
        classes: 'shepherd-button-primary'
      })
    }
    return buttons
  }

  cancelTour ({ silent = false } = {}) {
    if (!this.tour) return
    if (silent) {
      this.tour.off('cancel')
    }
    this.tour.cancel()
    this.tour = null
    this.activeTourId = null
  }

  async maybeNavigate (path, tourId, stepId, expectedTour) {
    if (!path) return
    if (window.location.pathname === path) return
    sessionStorage.setItem(`onboarding-navigate-${tourId}`, stepId)
    await new Promise(resolve => {
      document.addEventListener('turbo:load', resolve, { once: true })
      window.Turbo.visit(path)
    })
    if (this.tour !== expectedTour || this.activeTourId !== tourId) return
    this.navigatedResumeFor[tourId] = stepId
    await this.postProgress(`/onboarding/tours/${tourId}/advance`, { step_id: stepId })
  }

  async maybeOpenSidebar (selector) {
    if (window.innerWidth >= 768) return
    const sidebarNav = document.querySelector('[data-sidebar-target="menu"]')
    if (!sidebarNav) return
    const target = document.querySelector(selector)
    if (!target || !sidebarNav.contains(target)) return
    const sidebarEl = document.querySelector('[data-controller~="sidebar"]')
    if (!sidebarEl) return
    const sidebar = this.application.getControllerForElementAndIdentifier(sidebarEl, 'sidebar')
    if (!sidebar || sidebar.openedValue) return
    sidebar.openedValue = true
    const duration = parseFloat(getComputedStyle(sidebarEl).transitionDuration) * 1000 || 350
    await new Promise(resolve => setTimeout(resolve, duration))
  }

  waitForElement (selector, timeout = 1500) {
    return new Promise((resolve) => {
      if (document.querySelector(selector)) return resolve()
      const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
          observer.disconnect()
          resolve()
        }
      })
      observer.observe(document.body, { childList: true, subtree: true })
      setTimeout(() => { observer.disconnect(); resolve() }, timeout)
    })
  }

  async postProgress (url, body = {}) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrf || ''
      },
      body: JSON.stringify(body)
    }).catch((error) => {
      console.warn('[onboarding] progress POST failed', error)
    })
  }
}

stimulus.register('onboarding', OnboardingController)
